import { ComponentType, useMemo, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Box, Button, Chip, IconButton, Stack, SvgIconProps, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { createLookup, listLookup, LookupRecord, updateLookup } from '../../api/masterData';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { LookupFieldConfig, LookupFormDialog } from './LookupFormDialog';

interface LookupCrudPageProps {
  title: string;
  basePath: string;
  fields: LookupFieldConfig[];
  extraColumns?: GridColDef<LookupRecord>[];
  icon?: ComponentType<SvgIconProps>;
  color?: string;
}

export const LookupCrudPage = ({
  title,
  basePath,
  fields,
  extraColumns = [],
  icon = CategoryOutlinedIcon,
  color = '#5B2DA3',
}: LookupCrudPageProps) => {
  const queryClient = useQueryClient();
  const queryKey = ['lookup', basePath];

  const listQuery = useQuery({ queryKey, queryFn: () => listLookup(basePath) });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<LookupRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => createLookup(basePath, values),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: Record<string, unknown> }) =>
      updateLookup(basePath, id, values),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      updateLookup(basePath, id, { isActive }),
    onSuccess: invalidate,
  });

  const columns: GridColDef<LookupRecord>[] = useMemo(
    () => [
      ...extraColumns,
      {
        field: 'isActive',
        headerName: 'Status',
        width: 110,
        renderCell: (params) => (
          <Chip size="small" label={params.value ? 'Active' : 'Inactive'} color={params.value ? 'success' : 'default'} />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditingRecord(params.row);
                  setError(null);
                  setDialogOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={params.row.isActive ? 'Deactivate' : 'Activate'}>
              <IconButton
                size="small"
                onClick={() =>
                  toggleActiveMutation.mutate({ id: params.row.id, isActive: !params.row.isActive })
                }
              >
                {params.row.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [extraColumns, toggleActiveMutation],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={icon}
        color={color}
        title={title}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingRecord(null);
              setError(null);
              setDialogOpen(true);
            }}
          >
            New
          </Button>
        }
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={listQuery.data ?? []}
          columns={columns}
          loading={listQuery.isLoading}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
        />
      </Box>

      <LookupFormDialog
        open={dialogOpen}
        title={editingRecord ? `Edit ${title.replace(/s$/, '')}` : `New ${title.replace(/s$/, '')}`}
        fields={fields}
        editingRecord={editingRecord}
        errorMessage={error}
        onClose={() => setDialogOpen(false)}
        onSubmit={async (values) => {
          if (editingRecord) {
            await updateMutation.mutateAsync({ id: editingRecord.id, values });
          } else {
            await createMutation.mutateAsync(values);
          }
        }}
      />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
